import {memoizedCreateSelector} from 'config/selectorCaching'
import {getTaxesOrFeesExcluded} from 'modules/meta/selectors'
import {SORT_FIELD_PRICE} from 'modules/search/constants'

import type {State as SapiSearchState} from '../slice'
import type {RootState} from 'store'

/**
 * Creates a selector with multiple cache entries to let selectors with parameters (ie. hotelId or offerId) get cached properly
 */
const createSelector = memoizedCreateSelector

/**
 * Changes only when the price display needs a new SAPI search. Only the price
 * sort orders by priceDisplay, so every other sort re-renders the loaded offers.
 * Reads the input sort, which the URL sync updates together with the query
 * string, rather than searchedParameters, which lags one search behind.
 */
export const getPriceDisplaySearchKey = createSelector(
  [
    getTaxesOrFeesExcluded,
    (state: RootState) => state.sapiSearch?.searchParameters?.sortField,
    (state: RootState) => state.search?.showTotalPrices
  ],
  (taxesOrFeesExcluded, sortField, showTotalPrices) => {
    if (sortField !== SORT_FIELD_PRICE || !taxesOrFeesExcluded) return
    return showTotalPrices ? 'total' : 'nightly'
  }
)

/**
 * Get search parameters for current search or an empty object when uninitialized
 */
export const getInputSearchParameters = createSelector(
  [
    (state: RootState) => state.sapiSearch?.searchParameters,
    (state: RootState) => state.search?.showTotalPrices
  ],
  (searchParameters, showTotalPrices) => {
    const priceDisplay = showTotalPrices ? 'total' : 'nightly'

    const params = (searchParameters
      ? {...searchParameters, priceDisplay}
      : {}) as unknown as SapiSearchState['searchParameters']

    return {
      ...params,
      optimizeRooms: Boolean(params?.optimizeRooms)
    }
  }
)

/**
 * Get actual search parameters for current search
 */
export const getSearchParameters = createSelector(
  [
    getInputSearchParameters,
    (state: RootState) => state.sapiSearch?.searchedParameters
  ],
  (searchParameters, searchedParameters) =>
    searchedParameters || searchParameters
)

export const getSearchParametersRooms = createSelector(
  [getSearchParameters],
  ({rooms}) => rooms
)

export const getSearchParametersCheckIn = createSelector(
  [getSearchParameters],
  ({checkIn}) => checkIn
)

export const getSearchParametersCheckOut = createSelector(
  [getSearchParameters],
  ({checkOut}) => checkOut
)

export const getSearchParametersLabel = createSelector(
  [getSearchParameters],
  ({label}) => label
)

/**
 * Returns some of the search parameters for deep link generation
 */
export const getDeepLinkSearchParameters = createSelector(
  [getSearchParameters],
  searchParameters => {
    const {checkIn, checkOut, hotelId, placeId, rooms, searchId} =
      searchParameters ?? {}
    return {
      checkIn,
      checkOut,
      hotelId,
      placeId,
      rooms,
      searchId
    }
  }
)

export const getSearchParametersBoundingBox = createSelector(
  [getSearchParameters],
  searchParameters => searchParameters?.boundingBox
)
